import { useCallback } from 'react';
import { useAtom } from 'jotai';
import {
    setupIntentPopupOpenAtom,
    setupIntentPopupCustomerIdAtom,
    setupIntentPopupStripeAccountAtom,
    setupIntentPopupPaymentDataAtom,
    setupIntentPopupInvoicePaymentIdAtom,
    setupIntentPopupUrlSegmentAtom,
    setupIntentPopupPublishableKeyAtom,
} from '../states/setup-intent-popup-atoms';
import { applySurcharge, getSurchargeConfig } from '../util/utils';
import type { Currency } from '../type/types';

/**
 * Custom hook for managing setup intent popup state and logic
 * @returns Object containing popup state and control functions
 */
export const useSetupIntentPopup = () => {
    const [isSetupIntentPopupOpen, setIsSetupIntentPopupOpen] = useAtom(
        setupIntentPopupOpenAtom,
    );
    const [currentCustomerId, setCurrentCustomerId] = useAtom(
        setupIntentPopupCustomerIdAtom,
    );
    const [stripeAccount, setStripeAccount] = useAtom(
        setupIntentPopupStripeAccountAtom,
    );
    const [paymentData, setPaymentData] = useAtom(
        setupIntentPopupPaymentDataAtom,
    );
    const [invoicePaymentId, setInvoicePaymentId] = useAtom(
        setupIntentPopupInvoicePaymentIdAtom,
    );
    const [proposalUrlSegment, setProposalUrlSegment] = useAtom(
        setupIntentPopupUrlSegmentAtom,
    );
    const [publishableKey, setPublishableKey] = useAtom(
        setupIntentPopupPublishableKeyAtom,
    );

    const openPopup = useCallback(
        (
            customerId: number,
            proposalUrlSegmentParam: string,
            stripeAccountParam: string | null,
            paymentDataParam: { amount: number; currency: string },
            invoicePaymentIdParam?: string | null,
            publishableKeyParam?: string | null,
        ) => {
            const surchargeConfig = getSurchargeConfig(
                paymentDataParam?.currency as Currency,
            );
            setCurrentCustomerId(customerId);
            setProposalUrlSegment(proposalUrlSegmentParam);
            setIsSetupIntentPopupOpen(true);
            setStripeAccount(stripeAccountParam);
            setPaymentData({
                currency: paymentDataParam.currency,
                amount: paymentDataParam.amount,
                amountWithSurcharge: applySurcharge(
                    paymentDataParam.amount,
                    surchargeConfig,
                ),
                surchargeConfig: surchargeConfig,
            });
            setInvoicePaymentId(invoicePaymentIdParam || null);
            setPublishableKey(publishableKeyParam || null);
        },
        [
            setCurrentCustomerId,
            setIsSetupIntentPopupOpen,
            setStripeAccount,
            setPaymentData,
            setInvoicePaymentId,
            setProposalUrlSegment,
            setPublishableKey,
        ],
    );

    const closePopup = useCallback(() => {
        setIsSetupIntentPopupOpen(false);
        setCurrentCustomerId(0);
        setProposalUrlSegment('');
        setStripeAccount(null);
        setPaymentData(null);
        setInvoicePaymentId(null);
        setPublishableKey(null);
    }, [
        setIsSetupIntentPopupOpen,
        setCurrentCustomerId,
        setProposalUrlSegment,
        setStripeAccount,
        setPaymentData,
        setInvoicePaymentId,
        setPublishableKey,
    ]);

    return {
        isSetupIntentPopupOpen,
        openPopup,
        closePopup,
        currentCustomerId,
        proposalUrlSegment,
        stripeAccount,
        paymentData,
        invoicePaymentId,
        publishableKey,
    };
};
